import { useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCompare } from '../../../context/CompareContext';
import { allTools, Tool } from '../../../mocks/tools';
import { TOOL_PRICING } from '../../../mocks/tool-pricing';
import { TOOL_LOGOS } from '../../../mocks/tool-logos';

function getInitials(name: string): string {
  const words = name.replace(/[^a-zA-Z0-9\s]/g, '').trim().split(/\s+/);
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[1][0]).toUpperCase();
}

function ToolLogoImg({ slug, name, size }: { slug: string; name: string; size: 'sm' | 'md' | 'lg' }) {
  const [err, setErr] = useState(false);
  const logoUrl = TOOL_LOGOS[slug] ?? '';
  const dim = size === 'lg' ? 'w-16 h-16' : size === 'md' ? 'w-12 h-12' : 'w-8 h-8';
  const imgDim = size === 'lg' ? 'w-12 h-12' : size === 'md' ? 'w-9 h-9' : 'w-6 h-6';
  const textSize = size === 'lg' ? 'text-xl' : size === 'md' ? 'text-base' : 'text-xs';

  return (
    <div className={`${dim} rounded-xl bg-[#1a2235] border border-gray-700/50 flex items-center justify-center shrink-0 overflow-hidden`}>
      {logoUrl && !err ? (
        <img
          src={logoUrl}
          alt={`${name} logo`}
          width={size === 'lg' ? 48 : size === 'md' ? 36 : 24}
          height={size === 'lg' ? 48 : size === 'md' ? 36 : 24}
          className={`${imgDim} object-contain`}
          onError={() => setErr(true)}
        />
      ) : (
        <span className={`${textSize} font-bold text-gray-300 select-none`}>
          {getInitials(name)}
        </span>
      )}
    </div>
  );
}

function PricingBadge({ type }: { type: string }) {
  const styles: Record<string, string> = {
    Free: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
    Freemium: 'bg-teal-500/15 text-teal-300 border-teal-500/30',
    Paid: 'bg-orange-500/15 text-orange-300 border-orange-500/30',
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold border whitespace-nowrap ${styles[type] || 'bg-gray-500/15 text-gray-400 border-gray-500/30'}`}>
      {type}
    </span>
  );
}

function SectionHeader({ icon, title }: { icon: string; title: string }) {
  return (
    <div className="flex items-center gap-3 mb-4 px-1">
      <div className="w-8 h-8 flex items-center justify-center bg-teal-500/10 rounded-lg border border-teal-500/25 shrink-0">
        <i className={`${icon} text-teal-400 text-sm`}></i>
      </div>
      <h2 className="text-white font-bold text-base">{title}</h2>
      <div className="flex-1 h-px bg-gray-800/80"></div>
    </div>
  );
}

/* ─── Suggestion card ─── */
function SuggestionCard({ tool, onScrollToTop }: { tool: Tool; onScrollToTop: () => void }) {
  const [logoErr, setLogoErr] = useState(false);
  const { toggleTool, isInCompare, isFull, compareSlugs } = useCompare();
  const navigate = useNavigate();

  const pricing = TOOL_PRICING[tool.slug];
  const isAdded = isInCompare(tool.slug);
  const logoUrl = TOOL_LOGOS[tool.slug] ?? '';

  const handleAdd = () => {
    if (isAdded) return;
    if (isFull) return;
    toggleTool(tool.slug);
    onScrollToTop();
  };

  const swapUrl = `/compare?tools=${tool.slug},${compareSlugs[0] || ''}`;

  return (
    <div className="shrink-0 w-52 bg-gray-900/50 border border-gray-800/60 rounded-2xl p-4 flex flex-col gap-3 hover:border-gray-700/70 transition-all duration-200">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-[#1a2235] border border-gray-700/50 flex items-center justify-center overflow-hidden shrink-0">
          {logoUrl && !logoErr ? (
            <img
              src={logoUrl}
              alt={tool.name}
              width={32}
              height={32}
              className="w-8 h-8 object-contain"
              onError={() => setLogoErr(true)}
            />
          ) : (
            <span className="text-xs font-bold text-gray-300 select-none">{getInitials(tool.name)}</span>
          )}
        </div>
        <div className="min-w-0">
          <p className="text-white text-sm font-semibold leading-tight truncate">{tool.name}</p>
          <p className="text-gray-500 text-[10px] mt-0.5 truncate">{tool.category.replace('AI ', '')}</p>
        </div>
      </div>

      <p className="text-gray-400 text-xs leading-relaxed line-clamp-2 flex-1">{tool.description}</p>

      <div className="flex items-center gap-1.5 flex-wrap">
        {pricing && (
          <span className={`px-1.5 py-0.5 rounded-full text-[9px] font-bold border ${
            pricing === 'Free' ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' :
            pricing === 'Freemium' ? 'bg-teal-500/10 text-teal-400 border-teal-500/20' :
            'bg-orange-500/10 text-orange-400 border-orange-500/20'
          }`}>
            {pricing}
          </span>
        )}
        {tool.badge && (
          <span className={`px-1.5 py-0.5 rounded-full text-[9px] font-bold border ${
            tool.badge === 'featured' ? 'bg-amber-500/10 text-amber-400 border-amber-500/20' :
            tool.badge === 'trending' ? 'bg-rose-500/10 text-rose-400 border-rose-500/20' :
            'bg-emerald-500/10 text-emerald-400 border-emerald-500/20'
          }`}>
            {tool.badge === 'featured' ? '★ Featured' : tool.badge === 'trending' ? '↑ Trending' : '✦ New'}
          </span>
        )}
      </div>

      {!isFull ? (
        <button
          onClick={handleAdd}
          className="flex items-center justify-center gap-1.5 w-full py-2 bg-teal-500/15 hover:bg-teal-500 text-teal-400 hover:text-white text-xs font-bold rounded-xl transition-all duration-200 cursor-pointer whitespace-nowrap border border-teal-500/25 hover:border-teal-500 hover:shadow-lg hover:shadow-teal-500/20"
        >
          <i className="ri-add-line text-xs"></i>
          Add to Comparison
        </button>
      ) : (
        <Link
          to={swapUrl}
          className="flex items-center justify-center gap-1.5 w-full py-2 bg-gray-800/60 hover:bg-gray-700/60 text-gray-400 hover:text-white text-xs font-bold rounded-xl transition-all duration-200 cursor-pointer whitespace-nowrap border border-gray-700/40"
        >
          <i className="ri-arrow-left-right-line text-xs"></i>
          New comparison
        </Link>
      )}
    </div>
  );
}

/* ─── Pre-built comparison chip ─── */
function ComparisonChip({ slugs }: { slugs: string[] }) {
  const chipTools = slugs.map(s => allTools.find(t => t.slug === s)).filter(Boolean) as Tool[];
  if (chipTools.length < 2) return null;
  const url = `/compare?tools=${slugs.join(',')}`;

  return (
    <Link
      to={url}
      className="inline-flex items-center gap-2 px-3 py-2 bg-gray-900/50 hover:bg-gray-800/70 border border-gray-700/40 hover:border-teal-500/30 rounded-xl transition-all duration-200 cursor-pointer group"
    >
      <div className="flex -space-x-1.5">
        {chipTools.slice(0, 3).map(t => {
          const chipLogo = TOOL_LOGOS[t.slug] ?? '';
          return (
            <div key={t.slug} className="w-5 h-5 rounded-full bg-[#1a2235] border border-gray-700 overflow-hidden shrink-0 flex items-center justify-center">
              {chipLogo ? (
                <img
                  src={chipLogo}
                  alt={t.name}
                  width={16}
                  height={16}
                  className="w-4 h-4 object-contain"
                  onError={e => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
                />
              ) : (
                <span className="text-[8px] font-bold text-gray-400">{t.name[0]}</span>
              )}
            </div>
          );
        })}
      </div>
      <span className="text-gray-300 group-hover:text-white text-xs font-medium whitespace-nowrap transition-colors">
        {chipTools.map(t => t.name).join(' vs ')}
      </span>
      <i className="ri-arrow-right-line text-gray-600 group-hover:text-teal-400 text-xs transition-colors shrink-0"></i>
    </Link>
  );
}

/* ─── Main suggestions section ─── */
function RelatedSuggestions({ tools, onScrollToTop }: { tools: Tool[]; onScrollToTop: () => void }) {
  const { compareSlugs, isFull } = useCompare();

  // Get unique categories from current tools
  const currentCategories = useMemo(
    () => [...new Set(tools.map(t => t.category))],
    [tools]
  );

  // Find similar tools: same category, not already in comparison
  const similar = useMemo(() => {
    const currentSlugs = new Set(compareSlugs);
    const badgeOrder: Record<string, number> = { featured: 3, trending: 2, new: 1 };

    return allTools
      .filter(t => currentCategories.includes(t.category) && !currentSlugs.has(t.slug))
      .sort((a, b) => (badgeOrder[b.badge || ''] || 0) - (badgeOrder[a.badge || ''] || 0))
      .slice(0, 8);
  }, [compareSlugs, currentCategories]);

  // Build pre-made comparison suggestions
  // Each suggestion replaces/extends with one new tool + keeps 1-2 from current
  const prebuiltSuggestions = useMemo(() => {
    const suggestions: string[][] = [];
    const pool = similar.slice(0, 6);

    pool.forEach(newTool => {
      if (tools.length === 2) {
        // Suggest: newTool + tool[0]
        suggestions.push([newTool.slug, tools[0].slug]);
        // Suggest: newTool + tool[1]
        suggestions.push([newTool.slug, tools[1].slug]);
      } else if (tools.length >= 3) {
        // Suggest: newTool + first two current tools
        suggestions.push([newTool.slug, tools[0].slug, tools[1].slug]);
      }
    });

    // Deduplicate and take first 6
    const seen = new Set<string>();
    return suggestions.filter(slugs => {
      const key = [...slugs].sort().join(',');
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    }).slice(0, 6);
  }, [similar, tools]);

  if (similar.length === 0) return null;

  return (
    <section className="pt-6 border-t border-gray-800/60">
      {/* Header */}
      <div className="flex items-center gap-3 mb-5">
        <div className="w-8 h-8 flex items-center justify-center bg-amber-500/10 rounded-lg border border-amber-500/25 shrink-0">
          <i className="ri-lightbulb-line text-amber-400 text-sm"></i>
        </div>
        <div>
          <h2 className="text-white font-bold text-base leading-tight">You might also want to compare</h2>
          <p className="text-gray-500 text-xs mt-0.5">
            Similar tools in {currentCategories.map(c => c.replace('AI ', '')).join(' & ')}
          </p>
        </div>
        {isFull && (
          <span className="ml-auto flex items-center gap-1.5 px-3 py-1.5 bg-gray-800/60 border border-gray-700/40 rounded-lg text-gray-400 text-xs whitespace-nowrap">
            <i className="ri-information-line text-xs"></i>
            Remove a tool above to add more
          </span>
        )}
      </div>

      {/* Horizontally scrollable tool cards */}
      <div className="flex gap-3 overflow-x-auto pb-3 scrollbar-hide">
        {similar.map(tool => (
          <SuggestionCard key={tool.slug} tool={tool} onScrollToTop={onScrollToTop} />
        ))}
      </div>

      {/* Pre-built comparison chips */}
      {prebuiltSuggestions.length > 0 && (
        <div className="mt-5">
          <p className="text-gray-500 text-xs font-medium mb-3 flex items-center gap-2">
            <i className="ri-arrow-left-right-line text-gray-600"></i>
            Or jump into a related comparison:
          </p>
          <div className="flex flex-wrap gap-2">
            {prebuiltSuggestions.map((slugs, i) => (
              <ComparisonChip key={i} slugs={slugs} />
            ))}
          </div>
        </div>
      )}
    </section>
  );
}

interface Props {
  tools: Tool[];
  onBackToBrowser: () => void;
}

export default function ComparisonSection({ tools, onBackToBrowser }: Props) {
  const { removeTool } = useCompare();
  const navigate = useNavigate();
  const { clearAll } = useCompare();

  const toolCount = tools.length;
  const gridStyle = { gridTemplateColumns: `repeat(${toolCount}, minmax(0, 1fr))` };

  const handleRemove = (slug: string) => {
    removeTool(slug);
  };

  return (
    <div className="space-y-8">
      {/* Comparison header bar */}
      <div className="sticky top-0 z-30 bg-[#0a0a0a]/96 backdrop-blur-xl border-b border-gray-800/60 py-4 -mx-4 px-4">
        <div className="flex items-center gap-3 mb-3">
          <button
            onClick={onBackToBrowser}
            className="flex items-center gap-1.5 text-gray-400 hover:text-teal-400 text-sm font-medium transition-colors cursor-pointer"
          >
            <i className="ri-arrow-up-line text-xs"></i>
            Modify selection
          </button>
          <div className="flex-1 h-px bg-gray-800/60"></div>
          <button
            onClick={() => { clearAll(); navigate('/compare', { replace: true }); }}
            className="text-xs text-gray-600 hover:text-rose-400 transition-colors cursor-pointer flex items-center gap-1 whitespace-nowrap"
          >
            <i className="ri-delete-bin-line"></i>
            Clear
          </button>
        </div>

        <div
          className="grid gap-3"
          style={gridStyle}
        >
          {tools.map(tool => {
            const pricing = TOOL_PRICING[tool.slug];
            return (
              <div
                key={tool.slug}
                className="bg-gray-900/70 border border-gray-800/80 rounded-2xl p-3 flex flex-col gap-2"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-2.5 min-w-0">
                    <ToolLogoImg slug={tool.slug} name={tool.name} size="sm" />
                    <div className="min-w-0">
                      <h3 className="text-white font-bold text-xs leading-tight truncate">{tool.name}</h3>
                      <span className="text-teal-400 text-[10px] font-medium line-clamp-1">{tool.category.replace('AI ', '')}</span>
                    </div>
                  </div>
                  <button
                    onClick={() => handleRemove(tool.slug)}
                    className="w-5 h-5 flex items-center justify-center text-gray-600 hover:text-rose-400 hover:bg-rose-500/10 rounded-full transition-all cursor-pointer shrink-0"
                    title="Remove from comparison"
                  >
                    <i className="ri-close-line text-xs"></i>
                  </button>
                </div>
                <div className="flex flex-wrap gap-1">
                  {pricing && <PricingBadge type={pricing} />}
                </div>
                <a
                  href={tool.website}
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  className="flex items-center justify-center gap-1 w-full py-1.5 bg-teal-500 hover:bg-teal-600 text-white text-[10px] font-bold rounded-lg transition-all cursor-pointer whitespace-nowrap"
                >
                  Visit <i className="ri-external-link-line text-[9px]"></i>
                </a>
              </div>
            );
          })}
        </div>
      </div>

      {/* Overview */}
      <section>
        <SectionHeader icon="ri-file-text-line" title="Overview" />
        <div className="grid gap-4" style={gridStyle}>
          {tools.map(tool => (
            <div key={tool.slug} className="bg-gray-900/40 rounded-2xl border border-gray-800/60 p-5">
              <p className="text-gray-300 text-sm leading-relaxed">{tool.fullDescription || tool.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Key Features */}
      <section>
        <SectionHeader icon="ri-checkbox-circle-line" title="Key Features" />
        <div className="grid gap-4" style={gridStyle}>
          {tools.map(tool => (
            <div key={tool.slug} className="bg-gray-900/40 rounded-2xl border border-gray-800/60 p-5">
              {tool.features && tool.features.length > 0 ? (
                <ul className="space-y-2.5">
                  {tool.features.map((f, i) => (
                    <li key={i} className="flex items-start gap-2.5">
                      <i className="ri-checkbox-circle-fill text-teal-400 text-sm mt-0.5 shrink-0"></i>
                      <span className="text-gray-300 text-xs leading-relaxed">{f}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <span className="text-gray-500 text-sm italic">No features listed</span>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* Pricing */}
      <section>
        <SectionHeader icon="ri-price-tag-3-line" title="Pricing Plans" />
        <div className="grid gap-4" style={gridStyle}>
          {tools.map(tool => (
            <div key={tool.slug} className="bg-gray-900/40 rounded-2xl border border-gray-800/60 p-5">
              {tool.pricing && tool.pricing.length > 0 ? (
                <div className="space-y-3">
                  {tool.pricing.map((plan, i) => (
                    <div key={i} className={`pb-3 ${i < (tool.pricing?.length ?? 0) - 1 ? 'border-b border-gray-800/60' : ''}`}>
                      <div className="flex items-baseline justify-between gap-2 mb-1">
                        <span className="text-white text-xs font-bold">{plan.tier}</span>
                        <span className="text-teal-400 text-xs font-bold whitespace-nowrap">{plan.price}</span>
                      </div>
                      <p className="text-gray-400 text-xs leading-relaxed">{plan.note}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <span className="text-gray-500 text-sm italic">Check website for pricing</span>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* Pros */}
      <section>
        <SectionHeader icon="ri-thumb-up-line" title="Pros" />
        <div className="grid gap-4" style={gridStyle}>
          {tools.map(tool => (
            <div key={tool.slug} className="bg-gray-900/40 rounded-2xl border border-gray-800/60 p-5">
              {tool.pros && tool.pros.length > 0 ? (
                <ul className="space-y-2.5">
                  {tool.pros.map((p, i) => (
                    <li key={i} className="flex items-start gap-2.5">
                      <i className="ri-add-circle-fill text-teal-400 text-sm mt-0.5 shrink-0"></i>
                      <span className="text-gray-300 text-xs leading-relaxed">{p}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <span className="text-gray-500 text-sm italic">No pros listed</span>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* Cons */}
      <section>
        <SectionHeader icon="ri-thumb-down-line" title="Cons" />
        <div className="grid gap-4" style={gridStyle}>
          {tools.map(tool => (
            <div key={tool.slug} className="bg-gray-900/40 rounded-2xl border border-gray-800/60 p-5">
              {tool.cons && tool.cons.length > 0 ? (
                <ul className="space-y-2.5">
                  {tool.cons.map((c, i) => (
                    <li key={i} className="flex items-start gap-2.5">
                      <i className="ri-subtract-fill text-rose-400 text-sm mt-0.5 shrink-0"></i>
                      <span className="text-gray-300 text-xs leading-relaxed">{c}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <span className="text-gray-500 text-sm italic">No cons listed</span>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* Best For */}
      <section>
        <SectionHeader icon="ri-user-star-line" title="Best For" />
        <div className="grid gap-4" style={gridStyle}>
          {tools.map(tool => (
            <div key={tool.slug} className="bg-gray-900/40 rounded-2xl border border-gray-800/60 p-5">
              {tool.bestFor && tool.bestFor.length > 0 ? (
                <ul className="space-y-2.5">
                  {tool.bestFor.map((b, i) => (
                    <li key={i} className="flex items-start gap-2.5">
                      <i className="ri-user-star-fill text-teal-400 text-sm mt-0.5 shrink-0"></i>
                      <span className="text-gray-300 text-xs leading-relaxed">{b}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <span className="text-gray-500 text-sm italic">No info available</span>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* Get Started */}
      <section className="mt-4">
        <SectionHeader icon="ri-rocket-line" title="Get Started" />
        <div className="grid gap-4" style={gridStyle}>
          {tools.map(tool => {
            const pricing = TOOL_PRICING[tool.slug];
            return (
              <div
                key={tool.slug}
                className="bg-gradient-to-b from-teal-600/12 via-gray-900/60 to-gray-900/40 rounded-2xl border border-teal-500/20 p-6 text-center flex flex-col items-center"
              >
                <ToolLogoImg slug={tool.slug} name={tool.name} size="lg" />
                <h3 className="text-white font-bold text-base mt-3 mb-1">{tool.name}</h3>
                {pricing && <div className="mb-3"><PricingBadge type={pricing} /></div>}
                <p className="text-gray-400 text-xs leading-relaxed mb-4 line-clamp-2">{tool.description}</p>
                <a
                  href={tool.website}
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  className="inline-flex items-center justify-center gap-2 w-full px-4 py-3 bg-teal-500 hover:bg-teal-600 text-white text-sm font-bold rounded-xl transition-all hover:shadow-lg hover:shadow-teal-500/30 hover:scale-[1.02] whitespace-nowrap cursor-pointer"
                >
                  Try {tool.name}
                  <i className="ri-external-link-line text-xs"></i>
                </a>
                <Link
                  to={`/tool/${tool.slug}`}
                  className="mt-2 text-teal-400 hover:text-teal-300 text-xs font-medium transition-colors cursor-pointer"
                >
                  View full review →
                </Link>
              </div>
            );
          })}
        </div>
      </section>

      {/* Related suggestions */}
      <RelatedSuggestions tools={tools} onScrollToTop={onBackToBrowser} />
    </div>
  );
}
